'use client';

// npm library imports
import { useCallback, useEffect, useState } from 'react';

// style imports
import styles from './weather.module.scss';

// local imports
import { convertIcon, convertToFahrenheit } from './utils';
import { getWeatherData } from './actions';

// component imports
import Icons from '@components/Icons';

// lib imports
import { dateNowBris } from '@lib/functions/dateTimeFunctions';
import { WeatherData } from '@lib/types';

// Defines temperature unit types
type TemperatureUnit = 'celsius' | 'fahrenheit';

// Default weather data
const DEFAULT_WEATHER_DATA: WeatherData = {
    main: 'Sun',
    description: 'Sunshine',
    temp: 25,
    icon: '01d',
    date: 0,
};

const WeatherComponent = () => {
    // set default weather data
    const [weatherData, setWeatherData] =
        useState<WeatherData>(DEFAULT_WEATHER_DATA);

    // set default temperature unit
    const [temperatureUnit, setTemperatureUnit] =
        useState<TemperatureUnit>('celsius');

    // Date state to avoid hydration error
    const [currentDate, setCurrentDate] = useState<string | null>(null);

    // fetch weather data with server action from external weather API
    useEffect(() => {
        const fetchWeatherData = async () => {
            const fetchData = await getWeatherData();
            setWeatherData(fetchData);
        };

        fetchWeatherData();

        // set date on mount to avoid hydration error
        setCurrentDate(dateNowBris());
    }, []);

    // toggle temperature unit between celsius and fahrenheitF
    const toggleTemperatureUnit = useCallback(() => {
        setTemperatureUnit((prev: TemperatureUnit) =>
            prev === 'celsius' ? 'fahrenheit' : 'celsius',
        );
    }, []);

    // display correct temperature in the selected unit
    const displayTemperature =
        temperatureUnit === 'celsius'
            ? weatherData.temp
            : convertToFahrenheit(weatherData.temp);

    return (
        <div className={styles.container}>
            {/* weather icon */}
            <Icons
                iconClass={convertIcon(weatherData.icon)}
                externalStlyes={styles.icon}
                alt={weatherData.description}
            />
            {/* current date/time */}
            <p className={styles.date}>{currentDate}</p>
            {/* Temperature display */}
            <button
                type="button"
                className={`${styles.temp} ${styles[`${temperatureUnit}active`]}`}
                onClick={toggleTemperatureUnit}
                aria-label={`Switch temperature unit, currently ${temperatureUnit}`}
            >
                <span className={styles.tempnum}>
                    {Math.round(displayTemperature)}°{' '}
                </span>
                <span className={styles.cel}>C</span>
                <span> | </span>
                <span className={styles.fah}>F</span>
            </button>
            {/* Hidden html attribution to satisfy weather api terms and conditions */}
            <div
                dangerouslySetInnerHTML={{
                    __html: '<!-- Weather Data Provided by openweathermap.org -->',
                }}
            />
        </div>
    );
};

export default WeatherComponent;
